import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { getSocialAccounts, publishToSocial } from '../services/api';
import { GlassButton } from './ui/GlassButton';
import { Send, Clock, ExternalLink, CheckCircle, AlertCircle, X } from 'lucide-react';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface SocialPublishModalProps {
    taskId: string;
    projectId: string;
    platformContent: Record<string, any>;
    platformFilter?: string;  // e.g. 'facebook' — pre-filter to one platform
    onClose: () => void;
    onSuccess?: (postUrl: string) => void;
}

interface SocialAccountOption {
    account_id: string;
    platform: string;
    display_name: string | null;
    username: string | null;
    has_oauth: boolean;
}

const PLATFORM_LABELS: Record<string, string> = {
    facebook: 'Facebook',
    linkedin: 'LinkedIn',
    twitter: 'X (Twitter)',
    instagram: 'Instagram',
};

const SocialPublishModal: React.FC<SocialPublishModalProps> = ({
    taskId,
    projectId,
    platformContent,
    platformFilter,
    onClose,
    onSuccess,
}) => {
    const [accounts, setAccounts] = useState<SocialAccountOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedAccountId, setSelectedAccountId] = useState('');
    const [mode, setMode] = useState<'now' | 'schedule'>('now');
    const [scheduledFor, setScheduledFor] = useState('');
    const [link, setLink] = useState('');
    const [publishing, setPublishing] = useState(false);
    const [result, setResult] = useState<{ success?: boolean; post_url?: string; action?: string; error?: string } | null>(null);

    // Filter to platform-specific OAuth accounts
    const targetPlatform = platformFilter || 'facebook';
    useEffect(() => {
        getSocialAccounts(projectId)
            .then((data: SocialAccountOption[]) => {
                const filtered = data.filter(
                    (a: SocialAccountOption) => a.platform === targetPlatform && a.has_oauth
                );
                setAccounts(filtered);
                if (filtered.length === 1) {
                    setSelectedAccountId(filtered[0].account_id);
                }
            })
            .catch(() => setAccounts([]))
            .finally(() => setLoading(false));
    }, [projectId]);

    const pContent = platformContent?.[targetPlatform];
    const previewText = pContent
        ? [pContent.post || pContent.caption, pContent.cta, ...(pContent.hashtags || []).map((t: string) => `#${t.replace(/^#/, '')}`)].filter(Boolean).join('\n\n')
        : '';

    const handlePublish = async () => {
        if (!selectedAccountId) return;
        setPublishing(true);
        setResult(null);
        try {
            const res = await publishToSocial({
                task_id: taskId,
                platform: targetPlatform,
                account_id: selectedAccountId,
                mode,
                scheduled_for: mode === 'schedule' ? new Date(scheduledFor).toISOString() : undefined,
                link: link || undefined,
            });
            setResult(res);
        } catch (err: any) {
            setResult({ error: getApiError(err, 'Failed to publish') });
        } finally {
            setPublishing(false);
        }
    };

    // Min datetime for scheduler (10 min from now)
    const minDateTime = new Date(Date.now() + 10 * 60 * 1000).toISOString().slice(0, 16);

    const modal = (
        <div className="resync-modal-overlay" onClick={onClose}>
            <div className="resync-modal" onClick={e => e.stopPropagation()} style={{ width: 560 }}>
                <div className="resync-modal-header">
                    <h2>Publish to {PLATFORM_LABELS[targetPlatform] || targetPlatform}</h2>
                    <button className="resync-modal-close" onClick={onClose}><X size={18} /></button>
                </div>

                <div className="resync-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {result?.success || result?.action ? (
                        <div style={{ textAlign: 'center', padding: '20px 0' }}>
                            <CheckCircle size={40} style={{ color: '#16a34a', marginBottom: 12 }} />
                            {result.post_url ? (
                                <>
                                    <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', margin: '0 0 8px' }}>Published successfully!</p>
                                    <a href={result.post_url} target="_blank" rel="noopener noreferrer"
                                        style={{ color: 'var(--primary-500)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                        View post <ExternalLink size={14} />
                                    </a>
                                </>
                            ) : (
                                <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', margin: 0 }}>
                                    {result.action === 'queued' ? 'Post queued for approval' : 'Post scheduled successfully'}
                                </p>
                            )}
                        </div>
                    ) : result?.error ? (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: 12, background: '#fee2e2', borderRadius: 'var(--radius-md)', color: '#dc2626', fontSize: 13 }}>
                            <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                            {result.error}
                        </div>
                    ) : null}

                    {!result?.success && !result?.action && (
                        <>
                            {/* Account selector */}
                            <div>
                                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 6 }}>
                                    {PLATFORM_LABELS[targetPlatform] || targetPlatform} Account
                                </label>
                                {loading ? (
                                    <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading accounts...</p>
                                ) : accounts.length === 0 ? (
                                    <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No {PLATFORM_LABELS[targetPlatform] || targetPlatform} accounts connected. Connect one in Social Accounts first.</p>
                                ) : (
                                    <select
                                        value={selectedAccountId}
                                        onChange={e => setSelectedAccountId(e.target.value)}
                                        style={{
                                            width: '100%', padding: '8px 12px', fontSize: 14,
                                            border: '1px solid var(--stroke)', borderRadius: 'var(--radius-sm)',
                                            background: 'var(--panel)', color: 'var(--text)',
                                        }}
                                    >
                                        <option value="">Select a page...</option>
                                        {accounts.map(a => (
                                            <option key={a.account_id} value={a.account_id}>
                                                {a.display_name || a.username}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            {/* Content preview */}
                            <div>
                                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 6 }}>
                                    Post content
                                </label>
                                <div style={{
                                    padding: 12, fontSize: 13, lineHeight: 1.6,
                                    background: 'var(--panel-strong)', border: '1px solid var(--stroke)',
                                    borderRadius: 'var(--radius-md)', whiteSpace: 'pre-wrap',
                                    color: 'var(--text)', maxHeight: 200, overflowY: 'auto',
                                }}>
                                    {previewText || 'No Facebook content found in this task.'}
                                </div>
                            </div>

                            {/* Mode toggle */}
                            <div>
                                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 6 }}>
                                    When to publish
                                </label>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button
                                        onClick={() => setMode('now')}
                                        style={{
                                            flex: 1, padding: '10px 16px', fontSize: 13, fontWeight: 500,
                                            border: `2px solid ${mode === 'now' ? 'var(--primary-500)' : 'var(--stroke)'}`,
                                            borderRadius: 'var(--radius-md)', cursor: 'pointer',
                                            background: mode === 'now' ? 'var(--panel-strong)' : 'var(--panel)',
                                            color: mode === 'now' ? 'var(--primary-500)' : 'var(--text-secondary)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                        }}
                                    >
                                        <Send size={14} /> Publish Now
                                    </button>
                                    <button
                                        onClick={() => setMode('schedule')}
                                        style={{
                                            flex: 1, padding: '10px 16px', fontSize: 13, fontWeight: 500,
                                            border: `2px solid ${mode === 'schedule' ? 'var(--primary-500)' : 'var(--stroke)'}`,
                                            borderRadius: 'var(--radius-md)', cursor: 'pointer',
                                            background: mode === 'schedule' ? 'var(--panel-strong)' : 'var(--panel)',
                                            color: mode === 'schedule' ? 'var(--primary-500)' : 'var(--text-secondary)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                        }}
                                    >
                                        <Clock size={14} /> Schedule
                                    </button>
                                </div>
                            </div>

                            {/* Date picker for schedule mode */}
                            {mode === 'schedule' && (
                                <div>
                                    <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 6 }}>
                                        Scheduled date & time
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={scheduledFor}
                                        onChange={e => setScheduledFor(e.target.value)}
                                        min={minDateTime}
                                        style={{
                                            width: '100%', padding: '8px 12px', fontSize: 14,
                                            border: '1px solid var(--stroke)', borderRadius: 'var(--radius-sm)',
                                            background: 'var(--panel)', color: 'var(--text)',
                                        }}
                                    />
                                </div>
                            )}

                            {/* Optional link */}
                            <div>
                                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 6 }}>
                                    Link (optional)
                                </label>
                                <input
                                    type="url"
                                    value={link}
                                    onChange={e => setLink(e.target.value)}
                                    placeholder="https://example.com/article"
                                    style={{
                                        width: '100%', padding: '8px 12px', fontSize: 14,
                                        border: '1px solid var(--stroke)', borderRadius: 'var(--radius-sm)',
                                        background: 'var(--panel)', color: 'var(--text)',
                                        boxSizing: 'border-box',
                                    }}
                                />
                            </div>
                        </>
                    )}
                </div>

                <div className="resync-modal-footer">
                    {result?.success || result?.action ? (
                        <GlassButton variant="primary" onClick={() => {
                            if (result?.post_url && onSuccess) onSuccess(result.post_url);
                            onClose();
                        }}>Done</GlassButton>
                    ) : (
                        <>
                            <GlassButton variant="secondary" onClick={onClose}>Cancel</GlassButton>
                            <GlassButton
                                variant="primary"
                                onClick={handlePublish}
                                disabled={!selectedAccountId || !previewText || publishing || (mode === 'schedule' && !scheduledFor)}
                                icon={mode === 'now' ? <Send size={16} /> : <Clock size={16} />}
                            >
                                {publishing ? 'Publishing...' : mode === 'now' ? 'Publish Now' : 'Schedule'}
                            </GlassButton>
                        </>
                    )}
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default SocialPublishModal;
